import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ownerApi, type OwnerFileDocument } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import { Owner10HubShell } from '@/features/owner/components/Owner10HubShell';

const FOLDERS: Array<[string, string]> = [
  ['all', 'Все файлы'],
  ['requests', 'Заявки'],
  ['projects', 'Проекты'],
  ['doctoral', 'Докторантура'],
  ['publications', 'Публикации'],
  ['templates', 'Шаблоны'],
  ['delivery', 'Выдача / результаты'],
  ['legal', 'Соглашения / правовые'],
  ['governance', 'Промпты · контроль качества'],
  ['polls', 'Объявления / опросы'],
  ['events', 'Встречи / дежурства'],
  ['superai', 'Консультант владельца'],
  ['docs', 'Инструкции / документация'],
];

type DocItem = {
  id: string;
  folder: string;
  name: string;
  type: string;
  owner: string;
  ver: string;
  date: string;
};

const FOLDER_LABELS: Record<string, string> = Object.fromEntries(
  FOLDERS.filter(([id]) => id !== 'all').map(([id, title]) => [id, title]),
);

function mimeToType(mime: string): string {
  const map: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  };
  return map[mime] || mime.split('/').pop()?.toUpperCase() || 'FILE';
}

function formatDocDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function mapFileDocument(item: OwnerFileDocument): DocItem {
  return {
    id: item.id,
    folder: item.bucket,
    name: item.title,
    type: mimeToType(item.mime_type),
    owner: FOLDER_LABELS[item.bucket] || item.bucket,
    ver: `v${item.version}`,
    date: formatDocDate(item.effective_at),
  };
}

function fileHubUrl(params?: { fileId?: string; tab?: string }): string {
  const search = new URLSearchParams();
  if (params?.fileId) search.set('fileId', params.fileId);
  if (params?.tab) search.set('tab', params.tab);
  const query = search.toString();
  return query ? `${routes.owner}/files?${query}` : `${routes.owner}/files`;
}

/** Exact port of template documentsView() + renderDocs() — owner-uat (= .ps-*). */
export const OwnerSourceDocumentsPage = () => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [docs, setDocs] = useState<DocItem[]>([]);
  const [folder, setFolder] = useState('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    ownerApi
      .module('files')
      .then((payload) => {
        if (cancelled) return;
        const items = (payload.items as OwnerFileDocument[] | undefined) ?? [];
        setDocs(items.map(mapFileDocument));
      })
      .catch(() => {
        if (!cancelled) {
          setDocs([]);
          notify('Не удалось загрузить документы');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const list = useMemo(() => {
    return docs
      .filter((x) => folder === 'all' || x.folder === folder)
      .filter(
        (x) =>
          !query.trim() ||
          `${x.name} ${x.owner} ${x.type}`.toLowerCase().includes(query.trim().toLowerCase()),
      );
  }, [docs, folder, query]);

  const openDoc = openId ? docs.find((x) => x.id === openId) ?? null : null;
  const uniqueBuckets = useMemo(
    () => new Set(docs.map((doc) => doc.folder).filter(Boolean)).size,
    [docs],
  );
  const versionsToday = useMemo(() => {
    const today = new Date().toDateString();
    return docs.filter((doc) => {
      if (!doc.date || doc.date === '—') return false;
      try {
        return new Date(doc.date).toDateString() === today;
      } catch {
        return false;
      }
    }).length;
  }, [docs]);

  const openInFileHub = (fileId?: string, tab?: string) => {
    if (!fileId) return;
    navigate(fileHubUrl({ fileId, tab }));
  };

  return (
    <Owner10HubShell
      title="Исходные папки и документы"
      subtitle="Виртуальный реестр поверх канонического хранилища: ссылки на объекты, статусы и журнал — в структурированном состоянии"
      onClose={() => navigate(routes.owner)}
    >
      <div className="owner-uat-root">
        <section
          className="owner-uat-route-hero"
          style={{ ['--owner-uat-accent' as string]: '#49ddb7' }}
        >
          <div>
            <div className="owner-uat-route-kicker">Центр документов владельца</div>
            <h2>Исходные папки и документы</h2>
            <p>
              Виртуальный реестр поверх канонического хранилища. Файлы связываются с объектами CRM
              ссылками и метаданными; статусы, права и журнал остаются структурированным состоянием,
              а не отдельными файлами.
            </p>
          </div>
          <div className="owner-uat-actions">
            <button
              type="button"
              className="owner-uat-btn"
              onClick={() => navigate(fileHubUrl({ tab: 'versions_archive' }))}
            >
              Новая версия
            </button>
            <button
              type="button"
              className="owner-uat-btn primary"
              onClick={() => navigate(fileHubUrl())}
            >
              Открыть файловый хаб
            </button>
          </div>
        </section>

        <div className="owner-uat-kpis">
          <article className="owner-uat-kpi" style={{ ['--kpi' as string]: '#41d2ff' }}>
            <small>Коллекции</small>
            <strong>{uniqueBuckets}</strong>
            <span>виртуальные коллекции</span>
          </article>
          <article className="owner-uat-kpi" style={{ ['--kpi' as string]: '#49ddb7' }}>
            <small>Файлы</small>
            <strong>{docs.length}</strong>
            <span>в реестре владельца</span>
          </article>
          <article className="owner-uat-kpi" style={{ ['--kpi' as string]: '#e2ad3a' }}>
            <small>Новые версии</small>
            <strong>{versionsToday}</strong>
            <span>сегодня</span>
          </article>
          <article className="owner-uat-kpi" style={{ ['--kpi' as string]: '#ff7b65' }}>
            <small>Секреты</small>
            <strong>0</strong>
            <span>не раскрываются</span>
          </article>
        </div>

        <section className="owner-uat-panel">
          <header>
            <div>
              <h3>Центр документов</h3>
              <p>
                Поиск по файлу, объекту и версии. Докторантура поддерживает навигацию пользователь →
                досье → публикации → файл / версия.
              </p>
            </div>
            <span className="owner-uat-spacer" />
            <input
              className="owner-uat-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск файла…"
            />
          </header>
          <div className="owner-uat-panel-body">
            <div className="owner-uat-tree">
              <div className="owner-uat-folder-list">
                {FOLDERS.map(([id, title]) => {
                  const count =
                    id === 'all' ? docs.length : docs.filter((x) => x.folder === id).length;
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`owner-uat-folder${folder === id ? ' active' : ''}`}
                      onClick={() => setFolder(id)}
                    >
                      <span>▤</span>
                      {title}
                      <b>{count}</b>
                    </button>
                  );
                })}
              </div>
              <div>
                <div className="owner-uat-doc-row head">
                  <span>Файл</span>
                  <span>Коллекция</span>
                  <span>Тип</span>
                  <span>Изменён</span>
                  <span />
                </div>
                {list.map((x) => (
                  <div key={x.id} className="owner-uat-doc-row">
                    <strong>
                      {x.name}
                      <br />
                      <small style={{ color: '#7191a2' }}>
                        {x.id} · {x.ver}
                      </small>
                    </strong>
                    <span>{x.owner}</span>
                    <span>{x.type}</span>
                    <span>{x.date}</span>
                    <button type="button" className="owner-uat-btn" onClick={() => setOpenId(x.id)}>
                      Открыть
                    </button>
                  </div>
                ))}
                {!list.length ? <div className="owner-uat-empty">Файлов нет.</div> : null}
              </div>
            </div>
          </div>
        </section>
      </div>

      <div
        className={`owner-uat-drawer-backdrop${openDoc ? ' show' : ''}`}
        onClick={() => setOpenId(null)}
      />
      <aside className={`owner-uat-drawer${openDoc ? ' open' : ''}`} aria-hidden={!openDoc}>
        {openDoc ? (
          <>
            <header>
              <div style={{ flex: 1 }}>
                <small className="owner-uat-route-kicker">Паспорт файла</small>
                <h3 style={{ margin: '6px 0 0' }}>{openDoc.name}</h3>
                <p style={{ margin: '4px 0 0', color: '#86a4b3', fontSize: 11 }}>
                  {openDoc.id} · {openDoc.type}
                </p>
              </div>
              <button type="button" className="owner-uat-btn" onClick={() => setOpenId(null)}>
                Закрыть
              </button>
            </header>
            <div className="owner-uat-drawer-content">
              <div className="owner-uat-detail-grid">
                <div>
                  <small>Коллекция</small>
                  <strong>{openDoc.owner}</strong>
                </div>
                <div>
                  <small>Версия</small>
                  <strong>{openDoc.ver}</strong>
                </div>
                <div>
                  <small>Изменён</small>
                  <strong>{openDoc.date}</strong>
                </div>
                <div>
                  <small>Хранилище</small>
                  <strong>File Hub</strong>
                </div>
              </div>
              <div className="owner-uat-notice warn" style={{ marginTop: 14 }}>
                Замена файла создаёт новую версию в File Hub. Рабочий шаблон «последняя» переключается
                только после подтверждения.
              </div>
              <div className="owner-uat-actions" style={{ marginTop: 14, justifyContent: 'flex-start' }}>
                <button
                  type="button"
                  className="owner-uat-btn"
                  disabled={!openDoc.id}
                  onClick={() => openInFileHub(openDoc.id, 'library')}
                >
                  Открыть связанный объект
                </button>
                <button
                  type="button"
                  className="owner-uat-btn"
                  disabled={!openDoc.id}
                  onClick={() => openInFileHub(openDoc.id, 'versions_archive')}
                >
                  История версий
                </button>
                <button
                  type="button"
                  className="owner-uat-btn"
                  disabled={!openDoc.id}
                  onClick={() => openInFileHub(openDoc.id, 'library')}
                >
                  Скачать
                </button>
              </div>
            </div>
          </>
        ) : null}
      </aside>
    </Owner10HubShell>
  );
};
